import LogoWithBeta from '../BaseComponents/LogoWithBeta'
import { Button, SearchInput, TopBarActionsToolbar } from '../BaseComponents'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { FOCUS_VISIBLE_RING } from '../../lib/a11yFocus';
import routes from '../Router/routes';
import { userContext } from '../../context/UserContext';
import OrganisationSwitcher from '../OrganisationSwitcher/OrganisationSwitcher';
import { hasNoVisibleOrganisation } from '../../lib/sampleState';


function TopBar() {
	const navigate = useNavigate();
	const [searchString, setSearchString] = useState('');
	const [searchOpen, setSearchOpen] = useState(false);
	const formRef = useRef<HTMLFormElement>(null);
	const searchTriggerRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
	const wasOpen = useRef(false);

	// Phone layout only: move focus into the revealed field, and back to the icon when it closes.
	useEffect(() => {
		if (searchOpen) {
			formRef.current?.querySelector('input')?.focus();
		} else if (wasOpen.current) {
			searchTriggerRef.current?.focus();
		}
		wasOpen.current = searchOpen;
	}, [searchOpen]);

	const { userData } = useContext(userContext);

	const location = useLocation();

	// No organisation name when hidden sample/demo/training organisations leave nothing to show.
	const showOrganisation = !!userData?.access && !hasNoVisibleOrganisation(userData);
	const disabled = location.pathname === routes.secrSearch.path;

	const hideAuthButtons =
		location.pathname === routes.publicLogin.path ||
		location.pathname === routes.publicSignUp.path ||
		location.pathname === routes.forgotPassword.path ||
		location.pathname === routes.resetPassword.path ||
		location.pathname === routes.verifyEmail.path ||
		location.pathname === routes.acceptInvite.path ||
		location.pathname.startsWith('/invite/');

	const showAuthButtons = !userData && !hideAuthButtons;

	const doSearch = () => {
		const query = searchString.trim();
		if (!query) return;
		const qs = new URLSearchParams({s: query})
		navigate(`/public/profiles/?` + qs.toString());
		setSearchString('');
		setSearchOpen(false);
	}

	const handleSearch = (e: FormEvent) => {
		e.preventDefault();
		doSearch();
	}

	return (
		<div data-testid='top-bar-safe-area' className='bg-charcoal-700 pt-[env(safe-area-inset-top)] shrink-0'>
			<div
				data-testid='sidebar'
				className='py-sm px-3 md:px-lg bg-charcoal-700 flex flex-wrap items-center grow-0 shrink-0 min-h-[68px]'
			>
				<div className="flex-grow flex flex-wrap items-center">
					{/* Matches the sidebar's width (md:w-1/5) so the organisation selector starts at the body's left edge. */}
					<div className={showOrganisation ? 'shrink-0 -ml-lg pl-lg md:w-[20vw]' : ''}>
						<Link className={`inline-block rounded ${FOCUS_VISIBLE_RING}`} to="/">
							<LogoWithBeta className='my-lg' wrapperClassName='mx-0 md:mx-4' />
						</Link>
					</div>
					{showOrganisation && (
						<div className='min-w-[16rem] text-charcoal-050'>
							<OrganisationSwitcher />
						</div>
					)}
				</div>
				{/* Below md this row drops under the logo and takes the full width; from md up it sits inline. */}
				<form
					id='top-bar-search'
					role='search'
					ref={formRef}
					onSubmit={handleSearch}
					onKeyDown={e => { if (e.key === 'Escape') setSearchOpen(false) }}
					className={`${searchOpen ? 'flex' : 'hidden'} order-last basis-full items-center gap-2 py-2 md:order-none md:flex md:basis-auto md:ml-auto md:py-0 ${showAuthButtons ? 'md:mr-5' : ''}`}
				>
					<SearchInput
						name="globalSearch"
						placeholder="Search public emissions data"
						value={searchString}
						onChange={e => setSearchString(e.target.value)}
						onSearch={doSearch}
						disabled={disabled}
						className="flex-1 md:flex-none md:w-[345px]"
					/>
					<Button
						type='Link'
						icon='fa-solid fa-xmark'
						hasLeadingIcon
						accessibilityLabel='Close search'
						onClick={() => setSearchOpen(false)}
						additionalClass='md:hidden !text-white h-11 w-11'
					/>
				</form>
				<TopBarActionsToolbar className='ml-auto md:ml-0 !gap-2 md:!gap-5 !mr-0 md:!mr-4'>
					{!searchOpen && (
						<Button
							ref={searchTriggerRef}
							type='Link'
							icon='fa-solid fa-magnifying-glass'
							hasLeadingIcon
							accessibilityLabel='Search public emissions data'
							ariaExpanded={false}
							ariaControls='top-bar-search'
							disabled={disabled}
							onClick={() => setSearchOpen(true)}
							additionalClass='md:hidden !text-white h-11 w-11'
						/>
					)}

					{ showAuthButtons && (
						<div className='flex items-center self-end gap-2 md:gap-5'>
							<Button
								type="Primary"
								text="Log in"
								isLink
								url={routes.publicLogin.path}
								context="form-field"
							/>
							<Button
								type="Outlined"
								text="Sign up"
								isLink
								url={routes.publicSignUp.path}
								context="form-field"
							/>
						</div>
					)}
				</TopBarActionsToolbar>
			</div>
		</div>
	)
}

export default TopBar
